import { useEffect } from "react";
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  changeAccountColor,
  changeAccountSetting,
  getAccountAvailableUsers,
} from "api/accounts";
import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";

import { selectAccountById } from "@/entities/accounts/table";
import { addUsers, selectUsersById } from "@/entities/users/model";
import { updateAccount } from "@/actions/commonActions";
import { ReduxRootState } from "@/types/redux-store";

interface ColorParams {
  accountId: string;
  color: string;
  silent?: boolean;
}

interface UtmParams {
  enabled: boolean;
  name?: string | null;
  source?: string | null;
  medium?: string | null;
}

interface SaveAccountSettingsParams {
  accountId: string;
  changes: Record<string, unknown>;
  successMessage?: string;
}

interface PublishNotificationParams {
  accountId: string;
  publishNotificationUsersIds?: string[];
  publishEmailUsersIds?: string[];
}

type AvailableUser = {
  _id: string;
  firstName: string;
  lastName: string;
};

export type AccountManagingState = {
  availableUsersIds: string[];
  pending: {
    displayNameUpdating: boolean;
    colorUpdating: boolean;
    utmTagsUpdating: boolean;
    notificationSettingUpdating: boolean;
  };
};

const initialState: AccountManagingState = {
  availableUsersIds: [],
  pending: {
    displayNameUpdating: false,
    colorUpdating: false,
    utmTagsUpdating: false,
    notificationSettingUpdating: false,
  },
};

const featureKey = "accountManaging" as const;

function getFeatureState(state: ReduxRootState): AccountManagingState {
  return (
    (state as ReduxRootState & { [featureKey]?: AccountManagingState })[
      featureKey
    ] ?? initialState
  );
}

function getAccountPayload(
  state: ReduxRootState,
  accountId: string,
  changes: Record<string, unknown>
) {
  const account = selectAccountById(state, accountId);

  if (!account) {
    throw new Error(`Account ${accountId} was not found`);
  }

  return {
    ...account,
    ...changes,
  };
}

export const fetchAvailableUsers = createAsyncThunk(
  "accountManaging/fetchAvailableUsers",
  async (accountId: string, thunkApi) => {
    try {
      const users = (await getAccountAvailableUsers(
        accountId
      )) as AvailableUser[];

      thunkApi.dispatch(addUsers(users));

      return users.map((user) => user._id);
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

export const saveAccountSettings = createAsyncThunk(
  "accountManaging/saveAccountSettings",
  async (
    { accountId, changes, successMessage }: SaveAccountSettingsParams,
    thunkApi
  ) => {
    try {
      const state = thunkApi.getState() as ReduxRootState;
      const payload = getAccountPayload(state, accountId, changes);

      await changeAccountSetting(accountId, payload);
      thunkApi.dispatch(updateAccount(accountId, payload));

      if (successMessage) {
        toast.success(successMessage);
      }

      return payload;
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

export const saveAccountDisplayName = createAsyncThunk(
  "accountManaging/saveAccountDisplayName",
  async (
    {
      accountId,
      loginAlias,
      silent,
    }: { accountId: string; loginAlias: string; silent?: boolean },
    thunkApi
  ) => {
    return await thunkApi
      .dispatch(
        saveAccountSettings({
          accountId,
          changes: { loginAlias },
          successMessage: silent
            ? undefined
            : "Account was successfully updated",
        })
      )
      .unwrap();
  }
);

export const saveAccountColor = createAsyncThunk(
  "accountManaging/saveAccountColor",
  async ({ accountId, color, silent }: ColorParams, thunkApi) => {
    try {
      await changeAccountColor(accountId, color);
      thunkApi.dispatch(updateAccount(accountId, { color }));
      if (!silent) toast.success("Account color was successfully updated");

      return { accountId, color };
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

export const saveUtmTags = createAsyncThunk(
  "accountManaging/saveUtmTags",
  async (
    {
      accountId,
      utms,
      silent,
    }: { accountId: string; utms: UtmParams; silent?: boolean },
    thunkApi
  ) => {
    return await thunkApi
      .dispatch(
        saveAccountSettings({
          accountId,
          changes: { utms },
          successMessage: silent
            ? undefined
            : "UTM tag settings successfully updated",
        })
      )
      .unwrap();
  }
);

export const savePublishNotificationSettings = createAsyncThunk(
  "accountManaging/savePublishNotificationSettings",
  async (
    {
      accountId,
      publishNotificationUsersIds,
      publishEmailUsersIds,
    }: PublishNotificationParams,
    thunkApi
  ) => {
    const state = thunkApi.getState() as ReduxRootState;
    const account = selectAccountById(state, accountId);

    if (!account) {
      throw new Error(`Account ${accountId} was not found`);
    }

    const publishSettings = {
      ...(account.notificationSettings?.publish || {}),
      ...(publishNotificationUsersIds && {
        notification: publishNotificationUsersIds,
      }),
      ...(publishEmailUsersIds && { email: publishEmailUsersIds }),
    };

    return await thunkApi
      .dispatch(
        saveAccountSettings({
          accountId,
          changes: {
            notificationSettings: {
              ...(account.notificationSettings || {}),
              publish: publishSettings,
            },
          },
        })
      )
      .unwrap();
  }
);

const accountManagingSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {
    clearAvailableUsers(state) {
      state.availableUsersIds = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAvailableUsers.fulfilled, (state, action) => {
      state.availableUsersIds = action.payload;
    });

    builder.addCase(saveAccountDisplayName.pending, (state) => {
      state.pending.displayNameUpdating = true;
    });
    builder.addCase(saveAccountDisplayName.fulfilled, (state) => {
      state.pending.displayNameUpdating = false;
    });
    builder.addCase(saveAccountDisplayName.rejected, (state) => {
      state.pending.displayNameUpdating = false;
    });

    builder.addCase(saveAccountColor.pending, (state) => {
      state.pending.colorUpdating = true;
    });
    builder.addCase(saveAccountColor.fulfilled, (state) => {
      state.pending.colorUpdating = false;
    });
    builder.addCase(saveAccountColor.rejected, (state) => {
      state.pending.colorUpdating = false;
    });

    builder.addCase(saveUtmTags.pending, (state) => {
      state.pending.utmTagsUpdating = true;
    });
    builder.addCase(saveUtmTags.fulfilled, (state) => {
      state.pending.utmTagsUpdating = false;
    });
    builder.addCase(saveUtmTags.rejected, (state) => {
      state.pending.utmTagsUpdating = false;
    });

    builder.addCase(savePublishNotificationSettings.pending, (state) => {
      state.pending.notificationSettingUpdating = true;
    });
    builder.addCase(savePublishNotificationSettings.fulfilled, (state) => {
      state.pending.notificationSettingUpdating = false;
    });
    builder.addCase(savePublishNotificationSettings.rejected, (state) => {
      state.pending.notificationSettingUpdating = false;
    });
  },
});

export const accountManagingReducer = accountManagingSlice.reducer;
export const accountManagingActions = accountManagingSlice.actions;

export const selectAvailableUsers = createSelector(
  getFeatureState,
  selectUsersById,
  (accountManagingState, usersById) =>
    accountManagingState.availableUsersIds
      .map((userId) => usersById[userId])
      .filter(Boolean)
);

export const selectUpdatingStatuses = createSelector(
  getFeatureState,
  (accountManagingState) => accountManagingState.pending
);

export const selectSaveNotificationStatuses = createSelector(
  selectUpdatingStatuses,
  ({ notificationSettingUpdating }) => ({
    notificationSettingUpdating,
  })
);

export const selectFormDisabled = createSelector(
  selectUpdatingStatuses,
  ({ displayNameUpdating, colorUpdating, utmTagsUpdating }) =>
    displayNameUpdating || colorUpdating || utmTagsUpdating
);

export function useAccountManagingScope(accountId: string) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAvailableUsers(accountId) as never);

    return () => {
      dispatch(accountManagingActions.clearAvailableUsers());
    };
  }, [accountId, dispatch]);
}

export function useAccountManagingUsers() {
  return useSelector(selectAvailableUsers);
}

export function useAccountManagingStatuses() {
  return useSelector(selectUpdatingStatuses);
}

export function useAccountManagingFormDisabled() {
  return useSelector(selectFormDisabled);
}
